/**
 * Fed Photos utility functions for responsive image handling
 *
 * This module provides utilities to convert Rentals-style photo URLs
 * into responsive Fed Photos URLs using the cc_ft_* treatment family.
 *
 * The cc_ft_* treatments provide:
 * - 4:3 aspect ratio
 * - Center-crop "cover" behavior
 * - Optimized for responsive srcset
 * - WebP format for reduced bandwidth
 */

/**
 * Available Fed Photos treatment sizes (cc_ft_*)
 *
 * All sizes use 4:3 aspect ratio with center-crop cover behavior.
 * Service URL: http://on-demand-render-usw2.photos-prod.zg-int.net/help/view-treatments
 *
 * Available treatments:
 * - 96: 96x72 (rarely used - very small icons)
 * - 192: 192x144 (thumbnails, 2x DPR small images)
 * - 384: 384x288 (mobile listing cards 1x DPR, thumbnails 2x-4x DPR)
 * - 576: 576x432 (mobile listing cards 1.5x-2x DPR)
 * - 768: 768x576 (tablet/small desktop, mobile 2x-3x DPR)
 * - 960: 960x720 (desktop, mobile 3x DPR, fills gap for 1170px needs)
 * - 1152: 1152x864 (large desktop, high-DPR tablet)
 * - 1344: 1344x1008 (very large desktop, fills gap for 1200-1400px needs)
 * - 1536: 1536x1152 (maximum quality, high-DPR large displays)
 */
export type CcFtSize = 96 | 192 | 384 | 576 | 768 | 960 | 1152 | 1344 | 1536;

/**
 * Image format (webp is preferred, jpg for legacy fallback)
 */
export type ImageFormat = 'webp' | 'jpg';

/** Render hosts that serve Federated Photos treatments (dev / test / prod). */
const FED_PHOTO_HOSTS = new Set(['photos.dev.zillow.net', 'photos.test.zillow.net', 'photos.zillowstatic.com']);

/**
 * Whether a URL points at a Federated Photos render host. Callers that rewrite a
 * source URL into a treatment URL must guard on this first — an arbitrary
 * `/fp/<key>-...` path on a third-party/proxied host is not a Fed Photos image.
 */
export function isFederatedPhotoUrl(url: string): boolean {
  try {
    return FED_PHOTO_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
}

/**
 * Extracts the 32-character photoKey from a Rentals-style photo URL
 *
 * @example
 * extractPhotoKeyFromUrl('https://photos.zillowstatic.com/fp/4dab74e3819c511baaaa31c3ff0ee3a3-rentals_large_1200_1200.webp')
 * // returns '4dab74e3819c511baaaa31c3ff0ee3a3'
 *
 * @param url - The full photo URL from the API
 * @returns The 32-character photoKey or null if invalid
 */
export function extractPhotoKeyFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const fp = u.pathname.split('/fp/')[1]; // "4dab74e3...-rentals_large_1200_1200.webp"
    if (!fp) return null;
    const [photoKey] = fp.split('-'); // ["4dab74e3...", "rentals_large_1200_1200.webp"]
    return /^[a-f0-9]{32}$/.test(photoKey) ? photoKey : null;
  } catch {
    return null;
  }
}

/**
 * Builds a Fed Photos URL for a specific size and format
 *
 * @example
 * buildFedPhotoUrl('4dab74e3819c511baaaa31c3ff0ee3a3', 768)
 * // returns 'https://photos.zillowstatic.com/fp/4dab74e3819c511baaaa31c3ff0ee3a3-cc_ft_768.webp'
 *
 * @param photoKey - The 32-character photo identifier
 * @param size - The desired treatment size
 * @param ext - The image format (default: 'webp')
 * @param origin - The render host (default: prod). Pass the source photo's origin
 *   to keep dev/test listings on their own environment.
 * @returns The complete Fed Photos URL
 */
export function buildFedPhotoUrl(
  photoKey: string,
  size: CcFtSize,
  ext: ImageFormat = 'webp',
  origin = 'https://photos.zillowstatic.com',
): string {
  return `${origin}/fp/${photoKey}-cc_ft_${size}.${ext}`;
}

/**
 * Builds a srcset string with width descriptors for responsive image loading
 *
 * @example
 * buildSrcSet('4dab74e3819c511baaaa31c3ff0ee3a3')
 * // returns 'https://photos.zillowstatic.com/fp/4dab74e3...-cc_ft_384.webp 384w,
 * //          https://photos.zillowstatic.com/fp/4dab74e3...-cc_ft_768.webp 768w, ...'
 *
 * @param photoKey - The 32-character photo identifier
 * @param sizes - Array of sizes to include in srcset (default: [384, 768, 960, 1152, 1536])
 * @param ext - The image format (default: 'webp')
 * @returns The complete srcset string
 */
export function buildSrcSet(
  photoKey: string,
  sizes: Array<CcFtSize> = [384, 768, 960, 1152, 1536],
  ext: ImageFormat = 'webp',
): string {
  return sizes.map((s) => `${buildFedPhotoUrl(photoKey, s, ext)} ${s}w`).join(', ');
}

/**
 * Extracts photoKey and builds responsive image attributes from an API URL
 *
 * This is a convenience function that combines extraction and srcset building.
 *
 * @example
 * const attrs = buildResponsiveImageAttrs('https://photos.zillowstatic.com/fp/4dab...-rentals_large.webp')
 * // returns {
 * //   photoKey: '4dab74e3819c511baaaa31c3ff0ee3a3',
 * //   src: 'https://photos.zillowstatic.com/fp/4dab...-cc_ft_768.webp',
 * //   srcSet: 'https://photos.zillowstatic.com/fp/4dab...-cc_ft_384.webp 384w, ...'
 * // }
 *
 * @param apiUrl - The Rentals-style photo URL from the API
 * @param defaultSize - The default size for the src attribute (default: 768)
 * @param srcSetSizes - Sizes to include in srcset (default: [384, 768, 960, 1152, 1536])
 * @param ext - The image format (default: 'webp')
 * @returns Object with photoKey, src, and srcSet, or null if photoKey extraction fails
 */
export function buildResponsiveImageAttrs(
  apiUrl: string,
  defaultSize: CcFtSize = 768,
  srcSetSizes: Array<CcFtSize> = [384, 768, 960, 1152, 1536],
  ext: ImageFormat = 'webp',
): { photoKey: string; src: string; srcSet: string } | null {
  const photoKey = extractPhotoKeyFromUrl(apiUrl);
  if (!photoKey) return null;

  return {
    photoKey,
    src: buildFedPhotoUrl(photoKey, defaultSize, ext),
    srcSet: buildSrcSet(photoKey, srcSetSizes, ext),
  };
}

/**
 * Extracts a flat array of photo URLs from a listing object, checking multiple
 * fields in priority order: largePhotos → photos → medPhotoUrls.
 *
 * Listings from different API endpoints populate different photo fields.
 * Full ListingDetails has largePhotos/photos/medPhotoUrls; SummaryListingDetails
 * (SRP) only has medPhotoUrls. This function normalises them into a single array.
 */
type ListingPhotoSource = {
  largePhotos?: Array<{ url?: string }>;
  photos?: Array<{ url?: string }>;
  medPhotoUrls?: Array<string>;
};

export function getListingPhotoUrls(listing: ListingPhotoSource | null | undefined): Array<string> {
  const fromLarge = listing?.largePhotos?.length
    ? (listing.largePhotos.map((p) => p.url).filter(Boolean) as Array<string>)
    : null;
  if (fromLarge?.length) return fromLarge;

  const fromPhotos = listing?.photos?.length
    ? (listing.photos.map((p) => p.url).filter(Boolean) as Array<string>)
    : null;
  if (fromPhotos?.length) return fromPhotos;

  return listing?.medPhotoUrls?.length ? listing.medPhotoUrls : [];
}

/**
 * Default sizes attribute for listing cards (grid layout)
 * Optimized for 2-3 column grid on desktop, single column on mobile
 */
export const LISTING_CARD_SIZES = '(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw';

/**
 * Default sizes attribute for listing card carousels
 * ListingCardV3 uses responsive widths:
 * - Mobile (< 436px "listingCardWidth" breakpoint): 100% of container (~90vw accounting for padding)
 * - Desktop (>= 436px): Fixed 402px (standard) or 298px (carousel cards)
 */
export const LISTING_CARD_CAROUSEL_SIZES = '(max-width: 436px) 90vw, 402px';

/**
 * Default sizes attribute for HDP photo gallery (full-screen images)
 * Always uses full viewport width
 */
export const HDP_GALLERY_SIZES = '100vw';

/**
 * Default sizes attribute for thumbnail images
 * Smaller images for previews and thumbnails
 */
export const THUMBNAIL_SIZES = '(max-width: 640px) 50vw, 200px';
